// Raw game.status codes as returned by the backend /api/scores endpoint.
const RAW_STATUS = {
    SCHEDULED: "1",
    PRE_GAME: "2",
    IN_PROGRESS: "3",
    IN_PROGRESS_CRITICAL: "4",
    GAME_OVER: "5",
    FINAL: "6",
    FINAL_ALT: "7",
    TBD: "8",
    POSTPONED: "9",
} as const;

// game.type codes.
const GAME_TYPE = {
    PRESEASON: 1,
    REGULAR_SEASON: 2,
    PLAYOFF: 3,
} as const;

// Derives a full set of boolean flags describing a game's status from its raw
// status/currentPeriod/type fields, so callers never need to compare against
// raw status strings directly.
export interface GameStatusInput {
    status?: string;
    currentPeriod?: number;
    type?: number;
}

function deriveStatus(game?: GameStatusInput | null) {
    const status = game?.status;
    const currentPeriod = game?.currentPeriod;
    const type = game?.type;

    const isScheduled = status === RAW_STATUS.SCHEDULED;
    const isPreGame = status === RAW_STATUS.PRE_GAME;
    const isTBD = status === RAW_STATUS.TBD;
    const isPostponed = status === RAW_STATUS.POSTPONED;
    const isLive = status === RAW_STATUS.IN_PROGRESS || status === RAW_STATUS.IN_PROGRESS_CRITICAL;
    const isFinal = status === RAW_STATUS.GAME_OVER || status === RAW_STATUS.FINAL || status === RAW_STATUS.FINAL_ALT;
    const isNotYetStarted = isScheduled || isPreGame || isTBD || isPostponed;
    // A narrower subset of "final": excludes the bare "game over" status (5),
    // which the UI treats as not-yet-eligible for recap/extended video links.
    const isFinalWithLinks = status === RAW_STATUS.FINAL || status === RAW_STATUS.FINAL_ALT;

    // OT/shootout detection is only meaningful once a game has actually finished
    // (status "7"), inferred from currentPeriod/type the same way the original
    // inline logic in Scores.js did.
    const isOvertime = status === RAW_STATUS.FINAL_ALT &&
        ((type === GAME_TYPE.PLAYOFF && currentPeriod !== undefined && currentPeriod > 3) || currentPeriod === 4);
    const isShootout = status === RAW_STATUS.FINAL_ALT &&
        type === GAME_TYPE.REGULAR_SEASON && currentPeriod === 5;

    return {
        isScheduled,
        isPreGame,
        isTBD,
        isPostponed,
        isLive,
        isFinal,
        isFinalWithLinks,
        isNotYetStarted,
        isOvertime,
        isShootout,
    };
}

class GameStatus {
    static RAW_STATUS = RAW_STATUS;
    static GAME_TYPE = GAME_TYPE;
    static deriveStatus = deriveStatus;
}

export default GameStatus;
export { RAW_STATUS, GAME_TYPE, deriveStatus };
export type DerivedStatus = ReturnType<typeof deriveStatus>;
